import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldRange, RangeSliderExtended, SelectWithSearch } from 'ui';
import { FiltersData, InstrumentsInitialFiltersData, RangeFilter } from '../../types';
import { MaturityDateSelect } from './MaturityDateFilter';
import {
  updateFilters,
  minMaxValueInitialValues,
  rangeSliderValuesGenerator,
  valueParser,
} from '../../utils';

type DatatableInstrumentsFiltersProps = {
  filterFieldsInitialData: InstrumentsInitialFiltersData;
  prevFiltersValues: FiltersData;
  handleSubmit: (value: FiltersData) => void;
};

export const DatatableInstrumentsFilters: FC<DatatableInstrumentsFiltersProps> = ({
  filterFieldsInitialData,
  handleSubmit,
  prevFiltersValues,
}) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);
  const id = open ? 'datatable-filters-popover' : undefined;
  const [isFiltered, setIsFiltered] = useState(false);

  const [dataToSubmit, setDataToSubmit] = useState<FiltersData>({});

  useEffect(() => {
    setIsFiltered(!!Object.keys(prevFiltersValues).length);
    setDataToSubmit(prevFiltersValues ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevFiltersValues]);

  const { ratingType, rating, couponPercent, currencyCode, volume, maturityDate } =
    filterFieldsInitialData;

  const ratingParsed = valueParser(rating.length ? rating : []);
  const couponPercentParsed = useMemo(
    () => rangeSliderValuesGenerator(couponPercent, 0.01, '%'),
    [couponPercent],
  );
  const volumeParsed = () => (volume.minimum !== null ? [volume.minimum, volume.maximum] : []);

  const values = {
    ratingType: ratingType.map((element) => {
      return {
        label: t(`PORTFOLIO.${element}`),
        value: element,
      };
    }),
    rating: minMaxValueInitialValues(ratingParsed),
    couponPercent: minMaxValueInitialValues(couponPercentParsed),
    volume: volumeParsed(),
    currencyCode,
  };

  const handleSelectChange = (name: keyof FiltersData) => (value: any) =>
    setDataToSubmit(updateFilters(dataToSubmit, name, value));

  const handleRangeSliderChange = (name: string) => (newValues: RangeFilter) => {
    setDataToSubmit({
      ...dataToSubmit,
      [name]: newValues,
    });
  };

  const checkFiltersAvailability = () => {
    return (
      !!ratingType.length ||
      !!ratingParsed.length ||
      maturityDate.minimum ||
      !!couponPercentParsed.length ||
      !!volumeParsed().length ||
      !!currencyCode.length
    );
  };

  return (
    <>
      {checkFiltersAvailability() && (
        <Button
          size="small"
          variant={isFiltered ? 'contained' : 'outlined'}
          color={isFiltered ? 'primary' : undefined}
          onClick={handleClick}
        >
          {isFiltered ? t('Change filters') : t('Apply filters')}
        </Button>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} width="500px">
          {!!ratingType.length && (
            <SelectWithSearch
              label={t('PORTFOLIO.RATING_TYPE_ONELINE')}
              options={values.ratingType}
              value={dataToSubmit.ratingType ?? null}
              onChange={handleSelectChange('ratingType')}
            />
          )}
          {!!ratingParsed.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.INSTRUMENT_RATING')}
              options={ratingParsed}
              values={dataToSubmit.rating ?? values?.rating}
              onChange={handleRangeSliderChange('rating')}
            />
          )}
          {maturityDate.minimum && (
            <MaturityDateSelect
              maturityRange={maturityDate}
              prevValue={dataToSubmit.maturityDate ?? null}
              onChange={handleSelectChange('maturityDate')}
            />
          )}
          {!!couponPercentParsed.length && (
            <RangeSliderExtended
              label={t('PORTFOLIO.COUPON_RANGE')}
              options={couponPercentParsed}
              values={dataToSubmit.couponPercent ?? values?.couponPercent}
              onChange={handleRangeSliderChange('couponPercent')}
            />
          )}
          {!!volumeParsed().length && (
            <Box mt={2}>
              <FieldRange
                rangeLimit={volume}
                onChange={handleRangeSliderChange('volume')}
                values={dataToSubmit.volume ?? values?.volume}
                label={t('PORTFOLIO.INVESTMENT_VOLUME')}
              />
            </Box>
          )}
          {!!currencyCode.length && (
            <SelectWithSearch
              label={t('PORTFOLIO.CURRENCY')}
              options={values.currencyCode}
              value={dataToSubmit.currencyCode ?? null}
              onChange={handleSelectChange('currencyCode')}
            />
          )}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button type="submit" onClick={() => setDataToSubmit({})}>
              {t('RESET')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => {
                handleSubmit(dataToSubmit);
                handleClose();
              }}
            >
              {t('APPLY_FILTERS')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
